import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { getB2bGrantsAdmin, useQuery } from "wasp/client/operations";
import { Badge } from "../../../client/components/ui/badge";
import { Button } from "../../../client/components/ui/button";
import { Card, CardContent } from "../../../client/components/ui/card";
import { Input } from "../../../client/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../client/components/ui/select";
import useDebounce from "../../../client/hooks/useDebounce";
import useTranslation from "../../../client/hooks/useTranslation";
import type { Translations } from "../../../client/i18n/translations";
import { NewGrantDialog, RevokeGrantDialog, type PartnerOption } from "./dialogs";

const GRANT_STATUSES = ["pending", "active", "shadowed", "revoked"] as const;
type GrantStatus = (typeof GRANT_STATUSES)[number];
const ALL = "all";

function statusLabel(t: Translations, status: string): string {
  switch (status) {
    case "pending":
      return t.adminB2b.statusPending;
    case "active":
      return t.adminB2b.statusActive;
    case "shadowed":
      return t.adminB2b.statusShadowed;
    case "revoked":
      return t.adminB2b.statusRevoked;
    default:
      return status;
  }
}

function statusVariant(
  status: string,
): "default" | "outline" | "secondary" | "destructive" {
  switch (status) {
    case "active":
      return "default";
    case "shadowed":
      return "secondary";
    case "revoked":
      return "destructive";
    default:
      return "outline";
  }
}

function formatDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString() : "-";
}

export default function GrantsTable({
  partners,
  onChanged,
}: {
  partners: PartnerOption[];
  onChanged: () => void;
}) {
  const t = useTranslation();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>(ALL);
  const [partnerFilter, setPartnerFilter] = useState<string>(ALL);
  const [emailFilter, setEmailFilter] = useState("");
  const debouncedEmail = useDebounce(emailFilter.trim(), 300);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<{ id: string; email: string } | null>(
    null,
  );

  useEffect(
    function backToPageOne() {
      setPage(1);
    },
    [statusFilter, partnerFilter, debouncedEmail],
  );

  const { data, isLoading, refetch } = useQuery(getB2bGrantsAdmin, {
    skipPages: page - 1,
    ...(statusFilter !== ALL && { status: statusFilter as GrantStatus }),
    ...(partnerFilter !== ALL && { partnerId: partnerFilter }),
    ...(debouncedEmail !== "" && { emailFilter: debouncedEmail }),
  });

  const grants = data?.grants ?? [];
  const totalPages = Math.max(1, data?.totalPages ?? 1);

  const handleChanged = () => {
    refetch();
    onChanged();
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">{t.adminB2b.grantsTitle}</h2>
          <Button
            size="sm"
            onClick={() => setShowNewDialog(true)}
            disabled={partners.length === 0}
          >
            <Plus className="mr-1.5 h-4 w-4" aria-hidden />
            {t.adminB2b.newGrant}
          </Button>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t.adminB2b.filterStatusAll}</SelectItem>
              {GRANT_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {statusLabel(t, s)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {partners.length > 1 && (
            <Select value={partnerFilter} onValueChange={setPartnerFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t.adminB2b.filterPartnerAll}</SelectItem>
                {partners.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Input
            type="text"
            className="w-[260px]"
            placeholder={t.adminB2b.filterEmailPlaceholder}
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="py-2 pr-3">{t.adminB2b.colEmail}</th>
                <th className="py-2 pr-3">{t.adminB2b.colPartner}</th>
                <th className="py-2 pr-3">{t.adminB2b.colExternalRef}</th>
                <th className="py-2 pr-3">{t.adminB2b.colStatus}</th>
                <th className="py-2 pr-3">{t.adminB2b.colUser}</th>
                <th className="py-2 pr-3">{t.adminB2b.colActivatedAt}</th>
                <th className="py-2 pr-3">{t.adminB2b.colCreatedAt}</th>
                <th className="py-2 text-right">{t.adminB2b.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {grants.map((g) => (
                <tr key={g.id} className="border-b border-border/50">
                  <td className="py-2.5 pr-3 font-medium break-all">{g.email}</td>
                  <td className="py-2.5 pr-3">{g.partner.name}</td>
                  <td className="py-2.5 pr-3 font-mono text-xs">{g.externalRef ?? "-"}</td>
                  <td className="py-2.5 pr-3">
                    <Badge variant={statusVariant(g.status)}>
                      {statusLabel(t, g.status)}
                    </Badge>
                    {g.revokeAt && g.status !== "revoked" && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        {t.adminB2b.colEndsAt} {formatDate(g.revokeAt)}
                      </div>
                    )}
                  </td>
                  <td className="py-2.5 pr-3 text-muted-foreground break-all">
                    {g.user?.email ?? "-"}
                  </td>
                  <td className="py-2.5 pr-3 whitespace-nowrap text-muted-foreground">
                    {formatDate(g.activatedAt)}
                  </td>
                  <td className="py-2.5 pr-3 whitespace-nowrap text-muted-foreground">
                    {formatDate(g.createdAt)}
                  </td>
                  <td className="py-2.5 text-right whitespace-nowrap">
                    {g.status !== "revoked" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setRevokeTarget({ id: g.id, email: g.email })}
                      >
                        {t.adminB2b.revoke}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {!isLoading && grants.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-muted-foreground">
                    {t.adminB2b.noGrants}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-center justify-end gap-3">
          <Button
            size="sm"
            variant="outline"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label={t.adminB2b.previous}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </Button>
          <span className="text-sm text-muted-foreground">
            {t.adminB2b.page} {page} {t.adminB2b.of} {totalPages}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            aria-label={t.adminB2b.next}
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      </CardContent>
      <NewGrantDialog
        open={showNewDialog}
        onOpenChange={setShowNewDialog}
        partners={partners}
        onCreated={handleChanged}
      />
      <RevokeGrantDialog
        grant={revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onRevoked={handleChanged}
      />
    </Card>
  );
}
